import React, { useState, useRef, useCallback } from 'react';
import { auth } from '../../services/firebase';
import { ApiService } from '../../services/api';
import './VideoUploader.css';

interface VideoUploaderProps {
  onUploadComplete?: (filePath: string) => void;
}

const VideoUploader: React.FC<VideoUploaderProps> = ({ onUploadComplete }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadedPath, setUploadedPath] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const validateFile = (file: File): boolean => {
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska'];
    const maxSize = 10 * 1024 * 1024 * 1024; // 10GB

    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload MP4, MOV, AVI, or MKV files.');
      return false;
    }

    if (file.size > maxSize) {
      setError('File too large. Maximum size is 10GB.');
      return false;
    }

    return true;
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError('');

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        setSelectedFile(file);
      }
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      // Get signed upload URL from backend
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/gcp-virtual-production-lab/us-central1/api'}/upload/url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
        },
        body: JSON.stringify({
          fileName: selectedFile.name,
          fileType: selectedFile.type
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Upload URL error:', errorData);
        throw new Error(errorData.error || 'Failed to get upload URL');
      }

      const { data } = await response.json();
      const { uploadUrl, filePath } = data;

      // Upload file directly to Cloud Storage
      // For progress tracking, we'll use a simple fetch with periodic updates
      setUploadProgress(10); // Start progress
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      setUploadProgress(100); // Complete

      // Confirm upload with backend
      const confirmResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/gcp-virtual-production-lab/us-central1/api'}/upload/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
        },
        body: JSON.stringify({ filePath })
      });

      if (!confirmResponse.ok) {
        throw new Error('Failed to confirm upload');
      }

      setUploadedPath(filePath);
      if (onUploadComplete) {
        onUploadComplete(filePath);
      }

      // Reset after successful upload
      setTimeout(() => {
        setSelectedFile(null);
        setUploadProgress(0);
        setUploadedPath('');
      }, 3000);

    } catch (error: any) {
      console.error('Upload error:', error);
      setError(error.message || 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="video-uploader">
      <div
        className={`upload-zone ${isDragging ? 'dragging' : ''} ${selectedFile ? 'has-file' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !selectedFile && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {!selectedFile ? (
          <div className="upload-prompt">
            <div className="upload-icon">📹</div>
            <p className="upload-text">Drag and drop your video here</p>
            <p className="upload-subtext">or click to browse</p>
            <p className="upload-formats">Supported: MP4, MOV, AVI, MKV (max 10GB)</p>
          </div>
        ) : (
          <div className="file-info">
            <div className="file-icon">🎬</div>
            <h3>{selectedFile.name}</h3>
            <p>{formatFileSize(selectedFile.size)}</p>
            
            {uploadProgress > 0 && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            )}

            {uploadedPath && (
              <div className="success-message">
                ✅ Upload complete!
              </div>
            )}

            {!isUploading && !uploadedPath && (
              <div className="upload-actions">
                <button
                  className="upload-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    uploadFile();
                  }}
                >
                  Upload Video
                </button>
                <button
                  className="cancel-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setError('');
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="upload-error">
          ⚠️ {error}
        </div>
      )}
    </div>
  );
};

export default VideoUploader;